import { useEffect, useState } from 'react';
import {
  AffiliateCouponViewModel,
  APIResponseModel,
  AssessmentInputModel,
  AssessmentViewModel,
  CookieInputModel,
  CourseAccessUpdateModel,
  FileApiResponse,
  FileFormInputModel,
  GstInputModel,
  GSTViewModel,
  KnowledgeHubViewModel,
  NotificationEventMessage,
  ResourceCommentInputModel,
  ResourceCommentReactionInputModel,
  ResourceCommentReactionsViewModel,
  ResourceCommentReactionViewModel,
  ResourceCommentViewModel,
  ResourceLikesInputModel,
  ResourceLikesViewModel,
  TeamMemberInputModel,
} from '@models/api';
import { BrowserBaseClient } from './api-browser-base-client';

export class BrowserCustomerClient extends BrowserBaseClient {
  private readonly service: string = 'customer-api';
  private readonly knowledgeHubRoute: string = `${this.apiBaseUrl}/${this.service}/knowledgehub`;
  private readonly resourceRoute: string = `${this.apiBaseUrl}/${this.service}/resource`;
  private readonly profileRoute: string = `${this.apiBaseUrl}/${this.service}/profile`;
  private readonly quizRoute: string = `${this.apiBaseUrl}/${this.service}/quiz`;
  private readonly subscriptionRoute: string = `${this.apiBaseUrl}/${this.service}/subscriptions`;
  private readonly assessmentRoute: string = `${this.apiBaseUrl}/${this.service}/flow`;
  private readonly notificationsRoute: string = `${this.apiBaseUrl}/${this.service}/notification`;
  private readonly gstFlowRoute: string = `${this.apiBaseUrl}/${this.service}/claim-gst`;
  private readonly cookieRoute: string = `${this.apiBaseUrl}/${this.service}/cookie`;
  private readonly resultsRoute: string = `${this.apiBaseUrl}/${this.service}/results`;

  constructor() {
    super(window.__ENV__.API_URL);
  }

  public async GetBooks(offset: string, query?: string | null, tags?: string | null): Promise<APIResponseModel<KnowledgeHubViewModel[]>> {
    let url = `${this.knowledgeHubRoute}/books`;
    if (query && tags) {
      url = `${url}?q=${query}&t=${tags}&offset=${offset}`;
    } else if (query) {
      url = `${url}?q=${query}&offset=${offset}`;
    } else if (tags) {
      url = `${url}?t=${tags}&offset=${offset}`;
    } else {
      url = `${url}?offset=${offset}`;
    }
    return await this.publicGet<KnowledgeHubViewModel[]>(url);
  }

  public async GetVideos(): Promise<APIResponseModel<KnowledgeHubViewModel[]>> {
    const url = `${this.knowledgeHubRoute}/videos`;
    return await this.publicGet<KnowledgeHubViewModel[]>(url);
  }

  public async GetArticles(): Promise<APIResponseModel<KnowledgeHubViewModel[]>> {
    const url = `${this.knowledgeHubRoute}/articles`;
    return await this.publicGet<KnowledgeHubViewModel[]>(url);
  }

  public async GetTags(): Promise<APIResponseModel<KnowledgeHubViewModel[]>> {
    const url = `${this.knowledgeHubRoute}/tags`;
    return await this.publicGet<KnowledgeHubViewModel[]>(url);
  }

  public async GetResourceLikes(id: string): Promise<APIResponseModel<ResourceLikesViewModel>> {
    if (!id) throw new Error('Id is required');
    const url = `${this.knowledgeHubRoute}/resource/${id}/likes`;
    return await this.publicGet<ResourceLikesViewModel>(url);
  }

  public async GetResourceCommentReactions(id: string): Promise<APIResponseModel<ResourceCommentReactionsViewModel[]>> {
    if (!id) throw new Error('Comment id is required');
    const url = `${this.knowledgeHubRoute}/resource/${id}/reactions`;
    return await this.get<ResourceCommentReactionsViewModel[]>(url);
  }

  public async SaveResourceLike(req: ResourceLikesInputModel): Promise<APIResponseModel<void>> {
    if (!req.resourceId) throw new Error('Resource id is required');
    const url = `${this.resourceRoute}/likes`;
    return await this.post<ResourceLikesInputModel, void>(url, req);
  }

  public async GetResourceCommentSelfReaction(commentId: string): Promise<APIResponseModel<ResourceCommentReactionViewModel>> {
    if (!commentId) throw new Error('Comment id is required');
    const url = `${this.resourceRoute}/comments/${commentId}/reaction`;
    return await this.get<ResourceCommentReactionViewModel>(url);
  }

  public async SaveResourceCommentReaction(req: ResourceCommentReactionInputModel): Promise<APIResponseModel<ResourceCommentReactionViewModel>> {
    if (!req.resourceCommentId) throw new Error('Comment id is required');
    const url = `${this.resourceRoute}/comments/reactions`;
    return await this.post<ResourceCommentReactionInputModel, ResourceCommentReactionViewModel>(url, req);
  }

  public async UpdateResourceCommentReaction(req: ResourceCommentReactionInputModel): Promise<APIResponseModel<ResourceCommentReactionViewModel>> {
    if (!req.resourceCommentId) throw new Error('Comment id is required');
    const url = `${this.resourceRoute}/comments/reactions/update`;
    return await this.post<ResourceCommentReactionInputModel, ResourceCommentReactionViewModel>(url, req);
  }

  public async DeleteResourceCommentReaction(commentId: string): Promise<APIResponseModel<void>> {
    if (!commentId) throw new Error('Comment id is required');
    const url = `${this.resourceRoute}/comments/reactions/${commentId}`;
    return await this.delete<void>(url);
  }

  public async SaveResourceComment(req: ResourceCommentInputModel): Promise<APIResponseModel<ResourceCommentInputModel>> {
    if (!req.resourceId) throw new Error('Resource id is required');
    const url = `${this.resourceRoute}/comments`;
    return await this.post<ResourceCommentInputModel, ResourceCommentInputModel>(url, req);
  }

  public async ProfileImageUpload(fileInput: FileFormInputModel): Promise<APIResponseModel<FileApiResponse>> {
    const url = `${this.profileRoute}/upload-image`;
    return await this.uploadFormFile(url, fileInput);
  }

  public async GetNotifications(): Promise<APIResponseModel<NotificationEventMessage[]>> {
    const url = `${this.profileRoute}/notifications`;
    return await this.get<NotificationEventMessage[]>(url);
  }

  public async GetQuizOptionAnswer(optionId: string): Promise<APIResponseModel<boolean>> {
    if (!optionId) throw new Error('Option id is required');
    const url = `${this.quizRoute}/answer/${optionId}`;
    return await this.get<boolean>(url);
  }

  public async SubscribePlan(planId: string): Promise<APIResponseModel<void>> {
    if (!planId) throw new Error('Plan id is required');
    const url = `${this.subscriptionRoute}/plans/${planId}`;
    return await this.get<void>(url);
  }

  public async VerifyAffiliateCoupon(couponName: string, planId: string): Promise<APIResponseModel<AffiliateCouponViewModel>> {
    if (!couponName) throw new Error('Coupon Name is required');
    const url = `${this.subscriptionRoute}/verify-coupon/${couponName}/${planId}`;
    return await this.get<AffiliateCouponViewModel>(url);
  }

  public async SendJoinCommunityNotification(): Promise<APIResponseModel<void>> {
    const url = `${this.notificationsRoute}/community`;
    return await this.get<void>(url);
  }

  public async SendConsulationCallNotification(): Promise<APIResponseModel<void>> {
    const url = `${this.notificationsRoute}/consult-call`;
    return await this.get<void>(url);
  }

  public async GetNextQuestion(req: AssessmentInputModel): Promise<APIResponseModel<AssessmentViewModel>> {
    const url = `${this.assessmentRoute}/next-question`;
    return await this.post<AssessmentInputModel, AssessmentViewModel>(url, req);
  }

  public async IsSubscriptionActive(phone: string): Promise<APIResponseModel<boolean>> {
    if (!phone) throw new Error('Phone is required');
    const url = `${this.subscriptionRoute}/active/${phone}`;
    return await this.get<boolean>(url);
  }

  public async AddTeamMember(req: TeamMemberInputModel): Promise<APIResponseModel<string>> {
    const url = `${this.subscriptionRoute}/team-member`;
    return await this.post<TeamMemberInputModel, string>(url, req);
  }

  public async CheckConsultationCallStatus(): Promise<APIResponseModel<boolean>> {
    const url = `${this.notificationsRoute}/consult-call-status`;
    return await this.get<boolean>(url);
  }

  public async GetBucketFileUrl(req: { key: string }): Promise<APIResponseModel<string>> {
    const url = `${this.profileRoute}/bucket-file`;
    return await this.post<{ key: string }, string>(url, req);
  }

  public async SetGstStatus(req: GstInputModel): Promise<APIResponseModel<string>> {
    const url = `${this.gstFlowRoute}`;
    return await this.post<GstInputModel, string>(url, req);
  }

  public async VerifyGst(gstNo: string): Promise<APIResponseModel<GSTViewModel>> {
    const url = `${this.gstFlowRoute}/verify/${gstNo}`;
    return await this.get<GSTViewModel>(url);
  }

  public async SetCookieStatus(req: CookieInputModel): Promise<APIResponseModel<string>> {
    const url = `${this.cookieRoute}`;
    return await this.publicPost<CookieInputModel, string>(url, req);
  }

  public async SetSubscriptionHundred(orderId: string): Promise<APIResponseModel<string>> {
    if (!orderId) throw new Error('order Id is required');
    const url = `${this.subscriptionRoute}/discout-hundred/${orderId}`;
    return await this.post<{}, string>(url, {});
  }

  public async UpdateCourseAccess(data: CourseAccessUpdateModel): Promise<APIResponseModel<boolean>> {
      const url = `${this.resultsRoute}/update-access`;
      return await this.post< CourseAccessUpdateModel, boolean >(url, data);
    }

  public async GetInvoice(invoiceNo: string): Promise<APIResponseModel<string>> {
    const url = `${this.gstFlowRoute}/download-invoice/${invoiceNo}`;
    return await this.get<string>(url);
  }
  
}

export function useCustomerAPIClient() {
  const [client, setClient] = useState<BrowserCustomerClient | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setClient(new BrowserCustomerClient());
    }
  }, []);

  return client;
}
